import React, {useEffect, useState} from "react";
import {Swiper, SwiperSlide} from "swiper/react";
import {EffectCreative} from "swiper/modules";
import "swiper/css/effect-creative";
import "swiper/css";
import {useNavigate} from "react-router";
import {Loader} from "@/customComponents/uiComponents/Loader.jsx";
import {hopnLanguageArray} from "@/utils/functions.js";
import {parseAppZoneDate} from "@/utils/dateUtils";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {DownloadAppReminderContent} from "../Components/downloadAppReminderContent.jsx";
import {AirportPerksBar, PrebookCompareCard, PopularQuestionsSection} from "@/customComponents/sharedComponents/bookingPerks.jsx";
import {getFaqsForFlow} from "@/constants/faqs.js";
import {POLICY_CONSTANTS} from "@/constants/booking";
import {storage} from "@/utils/storage";
import {getBookingResult} from "@/state/bookingResult.js";
import {DISMISS_KEYS} from "@/constants/storageKeys.js";
import {img} from "@/utils/pluginImg.js";

// Booking-received tabs — reuse the CMR sliding segmented control (.hopn-cmr-tabs).
// Order matters twice over: the pills render in this order AND the index doubles as the
// slide index (onSelect → slideTo(i)). Reordering here means reordering the
// <SwiperSlide>s to match.
//
// Return Trip sits in the MIDDLE so it opens as the centred card of the creative stack
// (see initialSlide below) with a neighbour peeking on each side — as the first slide it
// would have had empty space on its left.
const BR_TABS = [
    // "Airport" is wrapped so MOBILE can drop it (.hopn-br-tab-word is display:none in
    // the 576 block) — three tabs plus the full wording overflow a phone-width strip.
    {key: 'perks', label: <><span className="hopn-br-tab-word">Airport </span>Perks & FAQs</>},
    {key: 'return', label: 'Return Trip'},
    {key: 'next', label: 'Next Steps'},
];

/**
 * Sliding segmented tabs (CMR control). Every card carries its own copy — with
 * partial slides visible the neighbours would otherwise show content with no
 * header — and each one drives the whole deck.
 *
 * `active` is the card's OWN key, not the deck's current slide: each card labels
 * itself, so card 2 reads "Airport Perks & FAQs" and card 3 "Next Steps" even
 * while card 1 is the one in view.
 */
function BrTabs({active, onSelect}) {
    return (
        <div className="hopn-cmr hopn-br-tabs-wrap">
            <div className="hopn-cmr-tabs" role="tablist">
                <span
                    className="hopn-cmr-tab-indicator"
                    style={{transform: `translateX(${BR_TABS.findIndex((t) => t.key === active) * 100}%)`}}
                    aria-hidden="true"
                />
                {BR_TABS.map((t, i) => (
                    <button
                        key={t.key}
                        type="button"
                        role="tab"
                        aria-selected={active === t.key}
                        className={`hopn-cmr-tab ${active === t.key ? 'active' : ''}`}
                        onClick={() => onSelect(i)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

// Mirrors the plugin's redesigned bookingReceived step. The created-trip data +
// trip_direction + compare figures live in memory (state/bookingResult, set by
// processingBooking) so they survive perk-step navigation even after the booking
// session keys are wiped below.
export function BookingReceived({setStep}) {
    const navigate = useNavigate();
    const [isViewTripLoading, setIsViewTripLoading] = useState(false);
    const [isReturnLoading, setIsReturnLoading] = useState(false);
    // "Share trip" clipboard-copy feedback (when the native share sheet is unavailable).
    const [shareCopied, setShareCopied] = useState(false);
    // Swiper instance for the card deck — lets the pill tabs jump to a slide.
    const [brSwiper, setBrSwiper] = useState(null);

    useEffect(() => {
        // The created-trip data now lives in memory (state/bookingResult), so there
        // is no sessionStorage key to preserve here — wipe the whole booking session.
        DISMISS_KEYS.forEach((key) => storage.remove(key));
        storage.clearAllCounters();
    }, []);

    // Trip direction captured at booking time (destinations are already wiped).
    const tripDirection = getBookingResult()?.trip_direction || 'others';
    // "Flight Delayed?" / airport wording is airport-only — hide it for city-to-city trips.
    const isAirportTrip = tripDirection !== 'others';

    // FAQs for the "Airport Perks & FAQs" tab — keyed off the booking's trip_direction.
    const faqs = getFaqsForFlow(tripDirection, {
        freeCancellationAvailable: POLICY_CONSTANTS.FREE_CANCELLATION_AVAILABLE,
        freeCancellationHours: POLICY_CONSTANTS.FREE_CANCELLATION_HOURS,
        freeEditsCount: POLICY_CONSTANTS.FREE_EDITS_COUNT,
        editableHoursBeforePickup: POLICY_CONSTANTS.EDITABLE_HOURS_BEFORE_PICKUP,
        waitingPickupFreeSeconds: POLICY_CONSTANTS.WAITING_PICKUP_FREE_SECONDS,
        waitingPickupRateCentsPerSecond: POLICY_CONSTANTS.WAITING_PICKUP_RATE_CENTS_PER_SECOND,
        waitingPickupMaxWaitSeconds: POLICY_CONSTANTS.WAITING_PICKUP_MAX_WAIT_SECONDS,
    });
    // Prebook-vs-Ride-Sharing figures captured at booking time (processingBooking) —
    // the live car/destinations are wiped by the DISMISS clear above.
    const compare = getBookingResult()?.compare || {};
    const comparePrice = compare.price || 0;
    // Ride-share figure was computed from the breakdown at booking time (our total
    // minus the online/coupon discounts) — it is NOT a markup on `price`.
    const compareShare = compare.share || 0;
    // Return Trip offer copy — the RETURN reverses the original direction:
    //  to-airport booking → return is FROM airport; from-airport → return is TO
    //  airport; others → show the reversed route (from dropoff back to pickup).
    const returnOfferText = tripDirection === 'to_airport'
        ? 'on your return trip from airport'
        : tripDirection === 'from_airport'
            ? 'on your return trip to airport'
            : (compare.pickup && compare.dropoff
                ? `on your return trip from ${compare.dropoff} to ${compare.pickup}`
                : 'on your return trip');

    // "Add a return" — go to the "add a return booking" page for the original
    // (parent) trip. In-app navigation (this IS the portal — no handoff needed).
    const handleHopnReturnBooking = () => {
        const parentTripId = getBookingResult()?.trip?.id;
        if (!parentTripId) {
            console.warn('[bookingReceived] no parent trip id resolved from tripData', getBookingResult());
            return;
        }
        setIsReturnLoading(true);
        navigate(`/return-booking/${parentTripId}`);
    };

    // "My Trip" — go to the trip detail (view) page for the trip just created.
    const handleViewTrip = () => {
        const tripId = getBookingResult()?.trip?.id;
        if (!tripId) {
            console.warn('[bookingReceived] no trip id for My Trip redirect', getBookingResult());
            return;
        }
        setIsViewTripLoading(true);
        navigate(`/rides/${tripId}`);
    };

    // "Share trip" — native share sheet where supported, else copy the message.
    // Builds a friendly blurb with the trip route + flat price.
    const handleShareTrip = async () => {
        const url = window.location.href;
        const LOC = {month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true};
        const pickupD = compare.scheduledAt ? new Date(String(compare.scheduledAt).replace(' ', 'T')) : null;
        const pickupText = pickupD && !isNaN(pickupD) ? pickupD.toLocaleString('en-US', LOC) : '';
        const arrivalText = pickupD && !isNaN(pickupD) && compare.durationMin
            ? new Date(pickupD.getTime() + compare.durationMin * 60000).toLocaleString('en-US', LOC) : '';
        const lines = ["🚘 I just booked my airport ride with Hop'n — it's fantastic!"];
        if (compare.pickup && compare.dropoff) lines.push(`📍 ${compare.pickup} → ${compare.dropoff}`);
        if (pickupText) lines.push(`🕒 Pickup: ${pickupText}`);
        if (arrivalText) lines.push(`🏁 Arrival: ${arrivalText}`);
        if (comparePrice) lines.push(`💰 Flat all-in price: $${comparePrice} — no surge, tip included, guaranteed pickup.`);
        lines.push("Book yours 👇");
        const text = lines.join("\n");
        try {
            if (navigator.share) {
                await navigator.share({title: "My Hop'n Ride", text, url});
            } else if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(`${text}\n${url}`);
                setShareCopied(true);
                setTimeout(() => setShareCopied(false), 2000);
            }
        } catch (_) { /* user cancelled / unsupported */ }
    };

    // "Add to calendar" — open a Google Calendar template for the booked ride.
    const handleAddToCalendar = () => {
        const trip = getBookingResult()?.trip || {};
        const title = "Hop'n Airport Ride";
        let dates = '';
        if (trip.scheduled_at) {
            // scheduled_at is 'Y-m-d H:i:s' in APP_TIMEZONE (Toronto), NOT UTC — the
            // backend runs on the same zone. Appending 'Z' claimed it was UTC and put the
            // calendar event out by the zone offset (a 7:00 AM Toronto pickup landed at
            // 3:00 AM). parseAppZoneDate resolves the true instant; Google wants UTC stamps,
            // which is exactly what toISOString then emits.
            const start = parseAppZoneDate(trip.scheduled_at);
            if (start && !isNaN(start.getTime())) {
                const end = new Date(start.getTime() + 60 * 60 * 1000);
                const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
                dates = `&dates=${fmt(start)}/${fmt(end)}`;
            }
        }
        const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}${dates}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <>
            {/* No fixed modal header here — the close (✕) moves onto the deck (rendered
                inline below), so the deck sits at the modal top and the shell's header
                slot stays empty (it collapses to zero height). */}
            <div className="hopn-booking-confirm modalPages-spacing padding-bottom-30" hopn-data-category="modal_booking_received">

                {/* Three-panel slider. Slide 1 carries the "Booking Received!" head + pill
                    tabs + Return-Trip offer; slides 2 and 3 are their own content. Pills call
                    slideTo(); drag/swipe also navigates (no dots). Plain slide effect — the
                    previous creative/stacked-card effect was removed, along with the manual
                    z-index bookkeeping it needed. autoHeight lets each panel size itself. */}
                <div className="hopn-br-deck">
                    {/* One close for the whole deck — the shared header with nothing but the
                        cross. On the deck rather than inside a card, so it stays put while
                        the cards scroll horizontally. */}
                    {/* The wrapper carries .hopn-br-close, not `extraClasses` — this
                        ModalHeader only reads extraClasses as a token bag (transparent,
                        grey, pt-0 …) and builds its own Tailwind class list, so custom
                        class names passed there never reach the DOM. */}
                    <div className="hopn-br-close">
                        <ModalHeader
                            inline={true}
                            title=""
                            extraClasses="transparent"
                            totalSteps={0}
                            cancelButton={true}
                            cancelButtonType="icon"
                            showBackButton={false}
                            setStep={setStep}
                            closeModal={() => window.location.reload()}
                        />
                    </div>

                    <Swiper
                        modules={[EffectCreative]}
                        effect="creative"
                        // Open on Return Trip (index 1 — the middle card), so the deck
                        // starts with a neighbour stacked on BOTH sides rather than a
                        // lopsided stack. Must match BR_TABS's order.
                        initialSlide={1}
                        grabCursor={true}
                        // Click-to-activate, so the deck isn't drag-only: clicking a peeked
                        // neighbour brings it to front. Swiper resolves the clicked slide from
                        // the event target, so the CSS has to let the click land on it — see
                        // the pointer-events rules on .hopn-br-swiper.
                        slideToClickedSlide={true}
                        speed={450}
                        longSwipesRatio={0.1}
                        // MUST stay false: creative overlaps the slides, so an auto height
                        // would be measured off the stack rather than the active card.
                        autoHeight={false}
                        watchSlidesProgress={true}
                        creativeEffect={{
                            limitProgress: 2,
                            // translate is a % of the SLIDE — and each slide spans the full
                            // modal — so the neighbour offset auto-scales with the modal/screen
                            // width: wide screen → they spread out, narrow → they tuck behind.
                            prev: {translate: ['-20%', 0, -180], scale: 0.84, opacity: 0.9},
                            next: {translate: ['20%', 0, -180], scale: 0.84, opacity: 0.9},
                        }}
                        onSwiper={setBrSwiper}
                        onProgress={(s) => {
                            s.slides.forEach((slide) => {
                                slide.style.zIndex = String(Math.round(50 - Math.abs(slide.progress) * 5));
                            });
                        }}
                        className="hopn-br-swiper"
                    >
                        {/* Slide 1 — Airport Perks & FAQs (same sections as
                            selectedCarDetailsLatest; .hopn-ride-latest scopes their styles). */}
                        <SwiperSlide className="hopn-ride-latest">
                            <div className="hopn-br-card">
                            <div className="modalPages-spacing">
                            {/* The perks bar IS this card's header. It also opens the compare
                                modal, so it's promoted above the tabs rather than sitting in the
                                panel behind a second, decorative copy of the same bar.
                                `portal` on every section here: these popups would otherwise be
                                trapped inside the swiper card (see PopupHost). */}
                            <AirportPerksBar price={comparePrice} pickup={compare.pickup || ''} dropoff={compare.dropoff || ''}
                                             sharePrice={compareShare} setStep={setStep} spacing={false} compact portal/>

                            <BrTabs active="perks" onSelect={(i) => brSwiper?.slideTo(i)}/>

                            <div className="hopn-br-panel hopn-ride-latest">
                                {/* Airport wording only on to-/from-airport bookings; a
                                    city-to-city trip falls back to the generic heading and
                                    no chip, since "Airport tips" is wrong there. */}
                                <PopularQuestionsSection faqs={faqs} setStep={setStep} spacing={false} portal
                                                         title={isAirportTrip ? "Exclusive Airport Pickup Perks" : undefined}
                                                         chip={isAirportTrip ? "Airport tips & FAQs" : ""}/>
                                <PrebookCompareCard price={comparePrice} pickup={compare.pickup || ''} dropoff={compare.dropoff || ''}
                                                    sharePrice={compareShare} setStep={setStep} spacing={false} portal/>
                            </div>
                            </div>{/* .modalPages-spacing */}
                            </div>
                        </SwiperSlide>

                        {/* Slide 2 — Return Trip: Booking Received head + tabs + the offer.
                            The slide spans the full modal; .hopn-br-card is the visible card. */}
                        <SwiperSlide>
                            <div className="hopn-br-card">
                            {/* Close lives on the deck, not on this card — see .hopn-br-close. */}
                            <div className="modalPages-spacing">
                            {/* Header card — booking received confirmation */}
                            <div className="hopn-br-head">
                                {/* Title sits in a white pill on the mint banner. */}
                                <h3 className="hopn-br-badge" hopn-data-index="booking_received_title" hopn-data-category="modal_booking_received">
                                    <span className="hopn-br-head-icon">
                                        <img decoding="async" src={img("green-tick.svg")} alt=""/>
                                    </span>
                                    <span className="hopn-br-dot" aria-hidden="true"></span>
                                    {hopnLanguageArray("modal_booking_received", "booking_received_title", "Booking Received!")}
                                </h3>

                                <div className="hopn-br-head-row">
                                    <p>We're matching you with the best driver. Confirmation coming shortly<span className="hopn-br-dots"><span></span><span></span><span></span></span></p>
                                    {/* Status badge, not a control — the match runs on its own. */}
                                    <span className="hopn-br-finding">Finding driver<span className="hopn-br-dots"><span></span><span></span><span></span></span></span>
                                </div>
                            </div>

                            <BrTabs active="return" onSelect={(i) => brSwiper?.slideTo(i)}/>

                            {/* Return Trip */}
                            <div className="hopn-br-panel">
                                {/* Heading → context chip → discount pill (mockup order). */}
                                <div className="hopn-br-offer">
                                    <span className="hopn-br-offer-tag">Exclusive Offer</span>
                                    <span className="hopn-br-offer-sub">{returnOfferText}</span>
                                    <span className="hopn-br-offer-amt">25% OFF</span>
                                </div>

                                <button
                                    type="button"
                                    className="hopn-br-return-cta"
                                    onClick={handleHopnReturnBooking}
                                    disabled={isReturnLoading}
                                    hopn-data-index="add_return_button"
                                    hopn-data-category="modal_booking_received"
                                >
                                    {isReturnLoading ? <Loader color="#fff"/> : (
                                        <>
                                            Add a return trip
                                            <span className="hopn-br-return-arrow" aria-hidden="true">
                                                <img decoding="async" src={img("arrow-right.svg")} alt=""/>
                                            </span>
                                        </>
                                    )}
                                </button>

                                <p className="hopn-br-note">Save 25% instantly when scheduling now</p>
                            </div>
                            </div>{/* .modalPages-spacing */}
                            </div>
                        </SwiperSlide>

                        {/* Slide 3 — Next Steps: status banner + download-app + actions */}
                        <SwiperSlide>
                            <div className="hopn-br-card">
                            <div className="modalPages-spacing">
                            {/* Same mint banner as slide 1; `is-plain` drops the black pill
                                so "Finding driver" reads as quiet status text here. */}
                            <div className="hopn-br-head is-plain">
                                {/* Here the pill spans the banner and carries the status on its
                                    right, unlike slide 1 where it's a separate black badge. */}
                                <h3 className="hopn-br-badge">
                                    <span className="hopn-br-head-icon">
                                        <img decoding="async" src={img("green-tick.svg")} alt=""/>
                                    </span>
                                    <span className="hopn-br-dot" aria-hidden="true"></span>
                                    {hopnLanguageArray("modal_booking_received", "booking_received_title", "Booking Received!")}
                                    <span className="hopn-br-finding">Finding driver<span className="hopn-br-dots"><span></span><span></span><span></span></span></span>
                                </h3>

                                <div className="hopn-br-head-row">
                                    <p>We're matching you with the best driver. Confirmation coming shortly<span className="hopn-br-dots"><span></span><span></span><span></span></span></p>
                                </div>
                            </div>

                            <BrTabs active="next" onSelect={(i) => brSwiper?.slideTo(i)}/>

                            <div className="hopn-br-panel">
                                <div className="hopn-br-next-card">
                                    <DownloadAppReminderContent showSubtitle={false}/>
                                    <div className="hopn-br-next-actions">
                                        <button type="button" onClick={handleViewTrip} disabled={isViewTripLoading}>
                                            {isViewTripLoading
                                                ? <Loader/>
                                                : <img decoding="async" src={img("small-bold-pencil.svg")} alt=""/>}
                                            My Trip
                                        </button>
                                        <button type="button" onClick={handleShareTrip}>
                                            <img decoding="async" src={img("small-bold-share.svg")} alt=""/>
                                            {shareCopied ? 'Link copied!' : 'Share trip'}
                                        </button>
                                        <button type="button" onClick={handleAddToCalendar}>
                                            <img decoding="async" src={img("small-bold-calender.svg")} alt=""/>
                                            Add to calendar
                                        </button>
                                    </div>
                                </div>
                            </div>
                            </div>{/* .modalPages-spacing */}
                            </div>
                        </SwiperSlide>

                        {/* Click-to-activate hit zones — the gutters either side of the centred
                            card, i.e. exactly where the previous/next cards peek out. Explicit
                            targets because Swiper's own slideToClickedSlide can't be relied on
                            here: every slide shell spans the FULL modal width, so which element
                            the browser hands a gutter click to depends on the stacking order,
                            not on which card is visible there.

                            slot="container-end" puts them INSIDE the swiper container, so their
                            pointer events still bubble to Swiper and dragging the gutter keeps
                            working — only the click is intercepted. */}
                        <button slot="container-end" type="button" aria-label="Previous card"
                                className="hopn-br-peek hopn-br-peek--prev"
                                onClick={() => brSwiper?.slidePrev()}/>
                        <button slot="container-end" type="button" aria-label="Next card"
                                className="hopn-br-peek hopn-br-peek--next"
                                onClick={() => brSwiper?.slideNext()}/>
                    </Swiper>
                </div>

            </div>
        </>
    );
}
export default BookingReceived;
